import React, { createContext, useState, useEffect, ReactNode } from 'react';
import axios from 'axios';

type AuthContextType = {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
  setIsAuthenticated: (newState: boolean) => void;
  hasPermission: (permission: any) => boolean;
  hasRole: (role: any) => boolean;
};

type AuthProviderProps = {
  children: ReactNode;
};

const initialValue = {
  isAuthenticated: false,
  isLoading: true,
  setIsAuthenticated: () => {},
  hasPermission: () => false,
  hasRole: () => false,
  user: null,
};

export interface User {
  userID: number;
  agentId: string;
  email: string;
  teams: string[];
  name: string;
  permissions: string[];
  roles: string[];
}

const AuthContext = createContext<AuthContextType>(initialValue);

const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(
    initialValue.isAuthenticated
  );

  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BASE_API_URL}/api/authentication/check-user`,
          {
            withCredentials: true,
          }
        );
        if (response.status === 200 && response.data.userID) {
          setIsAuthenticated(true);
          setUser(response.data);
        }
      } catch (error) {
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuthStatus();
  }, []);

  const hasPermission = (permission: string) =>
    !!user && user.permissions.includes(permission);
  const hasRole = (role: string) => !!user && user.roles.includes(role);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isLoading,
        setIsAuthenticated,
        hasPermission,
        hasRole,
        user,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export { AuthContext, AuthProvider };
